/**
 * Visual theme for the regional pages. The "chart sheet" look of the region chooser
 * (src/styles/theme-sheet.css) is the default, applied as `data-theme` on <html>, which is all the
 * theme stylesheet keys on; the page markup is untouched either way.
 *
 *   ?theme=default   the previous look, for comparison
 *   ?theme=sheet     back to the default
 *
 * `?theme=sheet` is kept in sessionStorage so it survives navigation within the tab. `?theme=default`
 * is a one-load preview: it is never persisted, and a "default" left in storage by an older build is
 * ignored in production, so a customer who once followed a QA link cannot get stuck on the untheme'd
 * look for the rest of the session. VITE_SITE_THEME=default flips the default for a whole deployment.
 *
 * The region of the page ("sg" | "us", from the URL) is also written as `data-region` on <html>,
 * so the theme stylesheet can tint one region without touching the other: /sg gets a hair cooler,
 * bluer paper (theme-sheet.css section 1), /us and the chooser keep the neutral sheet. Regions are
 * separate routers and every cross-region link is a full page load, so once per load is enough.
 */
import { getRegionFromPath } from "./region";

export const SITE_THEMES = ["sheet", "default"];
const DEFAULT_THEME = "sheet";
const STORAGE_KEY = "optra_site_theme";
const PARAM = "theme";

function readEnvDefault() {
  try {
    const v = String(import.meta.env?.VITE_SITE_THEME || "").trim().toLowerCase();
    return SITE_THEMES.includes(v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Resolve the theme for this page load (URL param wins, then the tab's stored choice, then the default) and apply it. */
export function applySiteTheme() {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  let theme = readEnvDefault();
  try {
    const requested = new URLSearchParams(window.location.search).get(PARAM);
    if (requested !== null) {
      const v = requested.trim().toLowerCase();
      if (SITE_THEMES.includes(v)) {
        theme = v;
        // Only the sheet look is remembered; "default" is a one-load preview.
        if (v === DEFAULT_THEME) window.sessionStorage.setItem(STORAGE_KEY, v);
        else window.sessionStorage.removeItem(STORAGE_KEY);
      }
    } else {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (stored === DEFAULT_THEME) theme = stored;
      else if (SITE_THEMES.includes(stored) && !import.meta.env?.PROD) theme = stored;
    }
  } catch {
    /* storage unavailable: keep the default */
  }
  // "default" means no attribute, so the theme stylesheet matches nothing.
  if (theme && theme !== "default") document.documentElement.dataset.theme = theme;
  else delete document.documentElement.dataset.theme;
  applyRegionAttribute();
  return theme;
}

/** `data-region="sg" | "us"` on <html> for the current URL; removed on the region-neutral pages. */
export function applyRegionAttribute(pathname = window.location.pathname) {
  if (typeof document === "undefined") return null;
  const region = getRegionFromPath(pathname);
  if (region) document.documentElement.dataset.region = region;
  else delete document.documentElement.dataset.region;
  return region;
}
